import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, TrendingUp, Info } from "lucide-react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Custom Tooltip
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-slate-900 border border-slate-700 rounded-xl px-4 py-3 shadow-xl">
        <p className="text-slate-400 text-xs mb-2 font-medium">Year {label}</p>
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full bg-emerald-500" />
            <span className="text-slate-300">Total Value:</span>
            <span className="font-semibold text-emerald-400">{fmt(data.total)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full bg-slate-500" />
            <span className="text-slate-300">Contributed:</span>
            <span className="font-semibold text-white">{fmt(data.contributed)}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="w-2 h-2 rounded-full bg-teal-400" />
            <span className="text-slate-300">Growth:</span>
            <span className="font-semibold text-teal-400">{fmt(data.growth)}</span>
          </div>
        </div>
      </div>
    );
  }
  return null;
};

const fmt = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(n);

const fmtShort = (n) => {
  if (n >= 1_000_000) return `£${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `£${(n / 1_000).toFixed(0)}k`;
  return fmt(n);
};

export default function CompoundInterest() {
  const [principal, setPrincipal] = useState(10000);
  const [monthlyContribution, setMonthlyContribution] = useState(500);
  const [rate, setRate] = useState(7);
  const [years, setYears] = useState(20);

  // Calculate final values
  const calculate = () => {
    let total = principal;
    for (let i = 0; i < years * 12; i++) {
      total = total * (1 + rate / 100 / 12) + monthlyContribution;
    }
    return total;
  };

  // Build chart data for growth projections
  const chartData = useMemo(() => {
    const data = [];
    let total = principal;
    
    // Add year 0
    data.push({
      year: 0,
      total: principal,
      contributed: principal,
      growth: 0,
    });
    
    for (let y = 1; y <= years; y++) {
      // Compound for 12 months
      for (let m = 0; m < 12; m++) {
        total = total * (1 + rate / 100 / 12) + monthlyContribution;
      }
      const contributed = principal + monthlyContribution * y * 12;
      data.push({
        year: y,
        total: Math.round(total),
        contributed: contributed,
        growth: Math.round(total - contributed),
      });
    }
    return data;
  }, [principal, monthlyContribution, rate, years]);

  const totalValue = calculate();
  const totalContributed = principal + monthlyContribution * years * 12;
  const totalGrowth = totalValue - totalContributed;

  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back */}
        <Link
          to="/tools"
          data-testid="back-to-tools"
          className="inline-flex items-center gap-2 text-slate-400 hover:text-white text-sm mb-8 transition-colors group"
        >
          <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          Back to Tools
        </Link>

        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-emerald-400" />
          </div>
          <span className="text-emerald-400 text-sm font-medium">Investing</span>
        </div>
        <h1
          data-testid="compound-interest-title"
          className="font-outfit text-3xl sm:text-4xl font-bold text-white mb-3 tracking-tight"
        >
          Compound Interest Calculator
        </h1>
        <p className="text-slate-400 mb-10">
          Project your investment growth over time. See the power of compounding with monthly contributions.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Inputs */}
          <div data-testid="compound-inputs" className="lg:col-span-2 space-y-6">
            <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 space-y-5">
              <h2 className="font-outfit font-semibold text-white text-lg">Your Inputs</h2>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Initial Investment</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-principal"
                    value={principal}
                    onChange={(e) => setPrincipal(Number(e.target.value))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">Monthly Contribution</label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">£</span>
                  <input
                    type="number"
                    data-testid="input-monthly"
                    value={monthlyContribution}
                    onChange={(e) => setMonthlyContribution(Number(e.target.value))}
                    className="w-full bg-slate-950 border border-slate-700 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 rounded-lg pl-7 pr-4 py-2.5 text-white text-sm outline-none transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Annual Return Rate: <span className="text-emerald-400 font-medium">{rate}%</span>
                </label>
                <input
                  type="range"
                  data-testid="input-rate"
                  min="1"
                  max="20"
                  step="0.5"
                  value={rate}
                  onChange={(e) => setRate(Number(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>1%</span><span>20%</span>
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-sm mb-1.5">
                  Time Horizon: <span className="text-emerald-400 font-medium">{years} years</span>
                </label>
                <input
                  type="range"
                  data-testid="input-years"
                  min="1"
                  max="50"
                  step="1"
                  value={years}
                  onChange={(e) => setYears(Number(e.target.value))}
                  className="w-full accent-emerald-500 cursor-pointer"
                />
                <div className="flex justify-between text-slate-600 text-xs mt-1">
                  <span>1yr</span><span>50yrs</span>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div data-testid="compound-results" className="lg:col-span-3 space-y-4">
            {/* Total */}
            <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-6">
              <p className="text-slate-400 text-sm mb-1">Total Portfolio Value</p>
              <p data-testid="result-total" className="font-outfit text-4xl font-bold text-emerald-400">
                {fmt(totalValue)}
              </p>
              <p className="text-slate-500 text-sm mt-1">after {years} years at {rate}% p.a.</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <p className="text-slate-500 text-xs mb-1.5">Total Contributed</p>
                <p data-testid="result-contributed" className="font-outfit text-2xl font-bold text-white">
                  {fmt(totalContributed)}
                </p>
              </div>
              <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
                <p className="text-slate-500 text-xs mb-1.5">Total Growth</p>
                <p data-testid="result-growth" className="font-outfit text-2xl font-bold text-emerald-400">
                  {fmt(totalGrowth)}
                </p>
              </div>
            </div>

            {/* Progress bar */}
            <div className="bg-slate-900/50 border border-slate-800 rounded-xl p-5">
              <p className="text-slate-400 text-sm mb-3">Contributions vs Growth</p>
              <div className="w-full bg-slate-800 rounded-full h-3 overflow-hidden">
                <div
                  data-testid="result-progress"
                  className="bg-emerald-500 h-3 rounded-full transition-all duration-500"
                  style={{ width: `${Math.min((totalContributed / totalValue) * 100, 100)}%` }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs">
                <span className="text-slate-500">Contributions ({Math.round((totalContributed / totalValue) * 100)}%)</span>
                <span className="text-emerald-400">Growth ({Math.round((totalGrowth / totalValue) * 100)}%)</span>
              </div>
            </div>

            {/* Growth Projections Chart */}
            <div data-testid="growth-chart" className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="font-outfit font-semibold text-white text-lg">Growth Projections</h3>
                  <p className="text-slate-500 text-sm">Portfolio value over time</p>
                </div>
                <div className="flex items-center gap-4 text-xs">
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1.5 rounded-full bg-emerald-500 inline-block" />
                    <span className="text-slate-400">Total Value</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className="w-3 h-1.5 rounded-full bg-slate-500 inline-block" />
                    <span className="text-slate-400">Contributed</span>
                  </div>
                </div>
              </div>

              {/* SVG Gradient Defs */}
              <svg width="0" height="0" style={{ position: "absolute" }}>
                <defs>
                  <linearGradient id="gradTotal" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#10b981" stopOpacity={0.01} />
                  </linearGradient>
                  <linearGradient id="gradContrib" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#64748b" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#64748b" stopOpacity={0.01} />
                  </linearGradient>
                </defs>
              </svg>

              <ResponsiveContainer width="100%" height={280}>
                <AreaChart
                  data={chartData}
                  margin={{ top: 10, right: 10, left: 10, bottom: 0 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#1e293b"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="year"
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickLine={false}
                    axisLine={{ stroke: "#1e293b" }}
                    label={{ value: "Years", position: "insideBottom", offset: -2, fill: "#475569", fontSize: 11 }}
                  />
                  <YAxis
                    tickFormatter={(v) => fmtShort(v)}
                    tick={{ fill: "#64748b", fontSize: 11 }}
                    tickLine={false}
                    axisLine={false}
                    width={60}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  {/* Contributed area (behind) */}
                  <Area
                    type="monotone"
                    dataKey="contributed"
                    stroke="#64748b"
                    strokeWidth={2}
                    fill="url(#gradContrib)"
                    dot={false}
                  />
                  {/* Total value area (front) */}
                  <Area
                    type="monotone"
                    dataKey="total"
                    stroke="#10b981"
                    strokeWidth={2.5}
                    fill="url(#gradTotal)"
                    dot={false}
                    activeDot={{ r: 5, fill: "#10b981", strokeWidth: 0 }}
                  />
                </AreaChart>
              </ResponsiveContainer>

              {/* Milestone markers */}
              <div className="grid grid-cols-3 gap-3 mt-4 pt-4 border-t border-slate-800">
                {[5, Math.floor(years / 2), years].filter((y, i, arr) => y <= years && arr.indexOf(y) === i).map((y) => {
                  const dataPoint = chartData.find(d => d.year === y);
                  if (!dataPoint) return null;
                  return (
                    <div key={y} className="bg-slate-800/30 rounded-lg p-3 text-center">
                      <p className="text-slate-500 text-xs">Year {y}</p>
                      <p className="font-outfit font-bold text-emerald-400 text-sm">{fmtShort(dataPoint.total)}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="flex items-start gap-2 bg-slate-900/30 border border-slate-800/50 rounded-xl p-4">
              <Info className="w-4 h-4 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-xs leading-relaxed">
                This calculator assumes monthly compounding and constant contributions. Figures are for illustrative purposes only and do not constitute financial advice.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
